// Miroir de backend/app/services/offer_calc.py : garder les deux calculs identiques.
// Le serveur reste la référence (totaux stockés, outils agents) ; ce calcul sert
// à l'affichage instantané pendant la saisie.

export const DEFAULT_PARAMS = {
  hourly_rate: 125,
  hours_per_day: 8,
  material_margin: 0.65,
  pm_rate: 0.1,
  client_discount: 0,
};

const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;
const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

const isSet = (value) => value !== null && value !== undefined && value !== "";

// rates = [TJM des paramètres, coefficient des paramètres, TJM en vigueur, coefficient en vigueur].
// Renvoie jours, achats, forfaits, écart de main-d'œuvre et écart d'achats margés (TJM ou marge propres).
const walk = (nodes, nodeTotals, rates) => {
  const [baseRate, baseMargin, rate, margin] = rates;
  const sums = [0, 0, 0, 0, 0];
  (nodes || []).forEach((node) => {
    const nodeRate = isSet(node.day_rate) ? num(node.day_rate) : rate;
    const nodeMargin = num(node.material_margin) > 0 ? num(node.material_margin) : margin;
    const quantity = num(node.quantity ?? 1);
    let sub;
    if (node.kind === "group") {
      sub = walk(node.children, nodeTotals, [baseRate, baseMargin, nodeRate, nodeMargin]).map((value) => value * quantity);
    } else {
      const days = quantity * num(node.days);
      const purchase = quantity * num(node.purchase);
      sub = [days, purchase, quantity * num(node.fixed),
        nodeRate !== baseRate ? days * (nodeRate - baseRate) : 0,
        nodeMargin !== baseMargin ? purchase * (1 / nodeMargin - 1 / baseMargin) : 0];
    }
    const net = sub[0] * baseRate + sub[1] / baseMargin + sub[2] + sub[3] + sub[4];
    nodeTotals[node.id] = {
      days: round2(sub[0]), purchase: round2(sub[1]), fixed: round2(sub[2]), net: round2(net),
      day_rate: nodeRate, material_margin: nodeMargin,
    };
    sub.forEach((value, index) => { sums[index] += value; });
  });
  return sums;
};

const KEYS = ["days", "pm_days", "hours", "labour", "purchase", "purchase_marged", "fixed", "net", "gross"];

export const computeEstimate = (content) => {
  const params = { ...DEFAULT_PARAMS, ...(content?.params || {}) };
  const discountFactor = 1 - num(params.client_discount);
  const baseRate = num(params.hourly_rate) * num(params.hours_per_day);
  const baseMargin = num(params.material_margin) > 0 ? num(params.material_margin) : 1;
  const nodes = {};
  const total = Object.fromEntries(KEYS.map((key) => [key, 0]));
  const required = Object.fromEntries(KEYS.map((key) => [key, 0]));
  const lots = (content?.lots || []).map((lot) => {
    const lotRate = isSet(lot.day_rate) ? num(lot.day_rate) : baseRate;
    const lotMargin = num(lot.material_margin) > 0 ? num(lot.material_margin) : baseMargin;
    const quantity = num(lot.quantity ?? 1);
    const [days, purchase, fixed, labourExtra, margedExtra] = walk(lot.children, nodes,
      [baseRate, baseMargin, lotRate, lotMargin]).map((value) => value * quantity);
    const pmRate = lot.include_pm === false ? 0 : num(params.pm_rate);
    const pmDays = lot.include_pm === false ? 0 : days * num(params.pm_rate);
    const hours = (days + pmDays) * num(params.hours_per_day);
    const labour = hours * num(params.hourly_rate) + labourExtra * (1 + pmRate);
    const purchaseMarged = purchase / baseMargin + margedExtra;
    const net = labour + purchaseMarged + fixed;
    const values = {
      days, pm_days: pmDays, hours, labour, purchase, purchase_marged: purchaseMarged,
      fixed, net, gross: discountFactor > 0 ? net / discountFactor : net,
    };
    KEYS.forEach((key) => {
      total[key] += values[key];
      if (!lot.optional) required[key] += values[key];
    });
    return {
      id: lot.id, name: lot.name || "", optional: Boolean(lot.optional), delay_weeks: lot.delay_weeks ?? null,
      ...Object.fromEntries(KEYS.map((key) => [key, round2(values[key])])),
      pm_labour: round2(pmDays * num(params.hours_per_day) * num(params.hourly_rate) + labourExtra * pmRate),
      quantity, unit: lot.unit || "",
      unit_net: quantity ? round2(net / quantity) : 0,
      unit_gross: quantity ? round2(values.gross / quantity) : 0,
    };
  });
  return {
    params, lots, nodes,
    total: Object.fromEntries(KEYS.map((key) => [key, round2(total[key])])),
    required: Object.fromEntries(KEYS.map((key) => [key, round2(required[key])])),
  };
};

const euroFormat = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const numberFormat = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 });

export const formatEuro = (value) => euroFormat.format(num(value));
// Prix unitaire : au centime près, sauf s'il tombe juste.
const unitEuroFormat = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });
export const formatUnitEuro = (value) => (Number.isInteger(num(value)) ? formatEuro(value) : unitEuroFormat.format(num(value)));
export const formatNumber = (value) => numberFormat.format(num(value));

let counter = 0;
export const newNodeId = (prefix = "n") => {
  counter += 1;
  return `${prefix}-${Date.now().toString(36)}${counter.toString(36)}${Math.random().toString(36).slice(2, 5)}`;
};
